/**
 * Custom Hooks for MDM Manager
 *
 * Purpose: Centralized state management and business logic for Master Data Management
 * Architecture: Custom React hooks for data fetching, filtering, selection, and dialog management
 * Dependencies: React, MDM cache service, Metadata service
 * Author: Development Team
 * Created: 2025-01-21
 * Updated: 2025-11-30 - Migrated to use MDM cache service
 */

import { PermissionKey } from '../constants/permissionKeys';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { metadataService, FieldDefinition } from '../services/metadataService';
import { MasterDataValue, ValueListFilters } from '../types/mdm';
import {
  useMDMScopedOptions,
  useMDMValues,
  useMDMCacheInvalidation,
  MDMFilterOptions,
} from '../services/mdmCacheService';

/**
 * Tenant Information Interface
 */
export interface TenantInfo {
  tenant_id: string;
  name: string;
  is_global: boolean;
  can_edit: boolean;
}

/**
 * Metadata Manager Context Interface
 */
export interface MetadataManagerContext {
  current_tenant: TenantInfo;
  available_tenants: TenantInfo[];
  user_permissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
}

/**
 * useMDMData Hook Return Type
 */
export interface UseMDMDataReturn {
  // Data
  context: MetadataManagerContext | null;
  fieldDefinitions: FieldDefinition[];
  values: MasterDataValue[];
  selectedFieldDef: string;
  selectedTenant: string;

  // Loading states
  loading: boolean;
  tableLoading: boolean;
  switchingContext: boolean;

  // Error state
  error: string | null;
  setError: (error: string | null) => void;

  // Actions
  setSelectedFieldDef: (fieldDefId: string) => void;
  setSelectedTenant: (tenantId: string) => void;
  loadMetadataManagerContext: (explicitTenantId?: string, signal?: AbortSignal) => Promise<void>;
  loadFieldDefinitions: (tenantId: string) => Promise<void>;
  loadValues: () => Promise<void>;
  handleTenantSwitch: (newTenantId: string) => Promise<void>;
  refreshData: () => void;
}

/**
 * useMDMData Hook
 *
 * Purpose: Manages data fetching for MDM Manager - tenant context, field definitions, and values
 *
 * @param initialFieldDefId - Optional initial field definition ID
 * @param initialTenantId - Optional initial tenant ID
 * @param filters - Value list filters (include_inactive, include_deleted, scope)
 * @param activeTenantId - Active tenant ID from auth context
 *
 * @returns Data, loading states, error state, and action handlers
 *
 * @example
 * const { context, values, loading, refreshData } = useMDMData(
 *   'field-123',
 *   'tenant-456',
 *   { include_inactive: true },
 *   user.activeTenant.tenant_id
 * );
 */
const VIEW_SCOPE_ERROR = 'Failed to load the master data you may see';

export function useMDMData(
  initialFieldDefId?: string,
  initialTenantId?: string,
  filters?: ValueListFilters,
  activeTenantId?: string
): UseMDMDataReturn {
  // State management
  const [selectedFieldDef, setSelectedFieldDef] = useState<string>(initialFieldDefId || '');
  const [selectedTenant, setSelectedTenant] = useState<string>(initialTenantId || '');
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [context, setContext] = useState<MetadataManagerContext | null>(null);

  // Loading states
  const [loading, setLoading] = useState<boolean>(true);
  const [switchingContext, setSwitchingContext] = useState<boolean>(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Refresh trigger for manual data refresh
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Cache invalidation for refresh functionality
  const { invalidateFielddef: _invalidateFielddef } = useMDMCacheInvalidation();

  // Convert ValueListFilters to MDMFilterOptions for cache
  const cacheFilterOptions: MDMFilterOptions = useMemo(
    () => ({
      includeInactive: filters?.include_inactive ?? true,
      includeDeleted: filters?.include_deleted ?? false,
      scope: filters?.scope,
      search: filters?.search,
    }),
    [filters?.include_inactive, filters?.include_deleted, filters?.scope, filters?.search]
  );

  // Use cached MDM values with client-side filtering
  const {
    data: cachedValues,
    isLoading: tableLoading,
    isError: _valuesError,
    error: _valuesErrorMessage,
    refetch: refetchValues,
  } = useMDMValues(selectedFieldDef, selectedTenant, cacheFilterOptions, {
    enabled: !!selectedFieldDef && !!selectedTenant,
  });

  // ORCH-332: the MDM Manager shows only values inside the caller's mdm.view scope
  // (ancestors stay, for navigation); each row's scope_info.can_edit says whether it
  // may be changed. Without the scope loaded yet, nothing out of scope flashes up.
  const {
    scope: viewScope,
    isLoading: scopeLoading,
    isError: viewScopeError,
    refetch: refetchViewScope,
  } = useMDMScopedOptions(selectedFieldDef, selectedTenant, PermissionKey.MDM_VIEW, {
    enabled: !!selectedFieldDef && !!selectedTenant,
    includeAll: true,
  });
  const values = useMemo(
    () => (viewScope ? cachedValues.filter((v) => viewScope.has(v.id)) : []),
    [cachedValues, viewScope]
  );
  // Say why the table is empty rather than showing no rows; clear it once a refetch works
  useEffect(() => {
    if (viewScopeError) {
      setError(VIEW_SCOPE_ERROR);
    } else {
      setError((current) => (current === VIEW_SCOPE_ERROR ? null : current));
    }
  }, [viewScopeError]);

  /**
   * Load metadata manager context from API
   *
   * Purpose: Fetch tenant context including current tenant, available tenants, and permissions
   * Logic:
   * - Calls metadata service to get manager context
   * - Sets context and selected tenant
   * - Loads field definitions for current tenant
   * - Handles cancellation via AbortSignal
   *
   * @param explicitTenantId - Optional specific tenant ID to load
   * @param signal - Optional AbortSignal for request cancellation
   *
   * @throws Error if context loading fails
   */
  const loadMetadataManagerContext = useCallback(
    async (explicitTenantId?: string, signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);

        // Check if request was cancelled
        if (signal?.aborted) {
          return;
        }

        // Get metadata manager context from API
        const response = await metadataService.getManagerContext(explicitTenantId);

        // Check again if request was cancelled before setting state
        if (!signal?.aborted) {
          if (response.data) {
            setContext(response.data);
            setSelectedTenant(response.data.current_tenant.tenant_id);

            // Load field definitions for the current tenant
            const fieldDefs = await metadataService.getFieldDefinitions({
              tenant_id: response.data.current_tenant.tenant_id,
            });
            setFieldDefinitions(fieldDefs);
          } else {
            throw new Error('No context data received from API');
          }
        }
      } catch (err) {
        // Only handle error if request wasn't cancelled
        if (!signal?.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load metadata manager context');
        }
      } finally {
        // Only set loading to false if request wasn't cancelled
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    []
  );

  /**
   * Load field definitions for current tenant
   *
   * Purpose: Fetch all field definitions available in the specified tenant context
   * Logic: Calls metadata service and updates field definitions state
   *
   * @param tenantId - Tenant ID to load field definitions for
   *
   * @throws Error logged to console if loading fails
   */
  const loadFieldDefinitions = useCallback(async (tenantId: string) => {
    try {
      const fieldDefs = await metadataService.getFieldDefinitions({
        tenant_id: tenantId,
      });
      setFieldDefinitions(fieldDefs);
    } catch (err: any) {
      console.error('Failed to load field definitions:', err);
    }
  }, []);

  /**
   * Load values for selected field definition and tenant
   *
   * Purpose: Trigger a refetch of cached MDM values
   * Logic: Now uses React Query cache - data is automatically fetched when
   *        selectedFieldDef and selectedTenant change. This function triggers
   *        a manual refetch if needed.
   *
   * Note: This is now a thin wrapper around refetchValues for backward compatibility
   */
  const loadValues = useCallback(async () => {
    if (!selectedFieldDef || !selectedTenant) return;
    // The view scope too: a value just created or changed must not be filtered out
    // by a stale scope (ORCH-332)
    await Promise.all([refetchValues(), refetchViewScope()]);
  }, [selectedFieldDef, selectedTenant, refetchValues, refetchViewScope]);

  /**
   * Handle tenant context switch
   *
   * Purpose: Switch the active tenant context and reload data
   * Logic:
   * - Calls API to switch context
   * - Updates local context state
   * - Loads field definitions for new tenant
   * - Preserves selected field definition if it exists in new context
   * - Clears field definition selection if it doesn't exist in new context
   *
   * @param newTenantId - ID of tenant to switch to
   *
   * @throws Error set in error state if context switch fails
   */
  const handleTenantSwitch = useCallback(
    async (newTenantId: string) => {
      if (!context || newTenantId === context.current_tenant.tenant_id) {
        return;
      }

      try {
        setSwitchingContext(true);
        setError(null);

        // Remember the currently selected field definition
        const previousFieldDef = selectedFieldDef;

        // Switch context using API
        await metadataService.switchContext(newTenantId);

        // Update context locally to maintain the selected tenant
        const newTenant = context.available_tenants.find((t) => t.tenant_id === newTenantId);
        if (newTenant) {
          setContext({
            ...context,
            current_tenant: {
              tenant_id: newTenant.tenant_id,
              name: newTenant.name,
              is_global: newTenant.is_global,
              can_edit: newTenant.can_edit,
            },
          });
          setSelectedTenant(newTenantId);

          // Load field definitions for the new tenant
          const newFieldDefs = await metadataService.getFieldDefinitions({
            tenant_id: newTenantId,
          });
          setFieldDefinitions(newFieldDefs);

          // Check if the previously selected field definition exists in the new tenant context
          if (previousFieldDef) {
            const fieldDefExists = newFieldDefs.some((fd) => fd.fielddef_id === previousFieldDef);
            if (fieldDefExists) {
              // Keep the same field definition selected
              setSelectedFieldDef(previousFieldDef);
            } else {
              // Clear the selection if the field definition doesn't exist in the new tenant
              setSelectedFieldDef('');
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to switch tenant context');
      } finally {
        setSwitchingContext(false);
      }
    },
    [context, selectedFieldDef]
  );

  /**
   * Refresh data by incrementing refresh trigger
   *
   * Purpose: Force reload of values without changing selections
   * Logic: Increments refresh trigger which triggers loadValues useEffect
   */
  const refreshData = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  // Load initial context - use main tenant context or URL parameter for testing
  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const testTenantId = urlParams.get('tenant_id');

      // Use main tenant context if no URL parameter is provided
      const mainTenantId = testTenantId || activeTenantId;
      await loadMetadataManagerContext(mainTenantId, abortController.signal);
    };

    loadData();

    // Cleanup function to abort the request if component unmounts or dependencies change
    return () => {
      abortController.abort();
    };
  }, [loadMetadataManagerContext, activeTenantId]);

  // Load values when selection or refresh trigger changes
  useEffect(() => {
    loadValues();
  }, [loadValues, refreshTrigger]);

  return {
    // Data
    context,
    fieldDefinitions,
    values,
    selectedFieldDef,
    selectedTenant,

    // Loading states
    loading,
    tableLoading: tableLoading || scopeLoading,
    switchingContext,

    // Error state
    error,
    setError,

    // Actions
    setSelectedFieldDef,
    setSelectedTenant,
    loadMetadataManagerContext,
    loadFieldDefinitions,
    loadValues,
    handleTenantSwitch,
    refreshData,
  };
}

/**
 * useMDMFilters Hook Return Type
 */
export interface UseMDMFiltersReturn {
  filters: ValueListFilters;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleFilterChange: (key: keyof ValueListFilters, value: any) => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * useMDMFilters Hook
 *
 * Purpose: Manages filter state and search functionality for MDM values
 *
 * @param selectedFieldDef - Currently selected field definition ID
 * @param selectedTenant - Currently selected tenant ID
 * @param initialFilters - Optional initial filter values
 *
 * @returns Filters state and change handlers
 *
 * @example
 * const { filters, searchTerm, handleFilterChange } = useMDMFilters(
 *   'field-123',
 *   'tenant-456',
 *   { include_inactive: true }
 * );
 */
export function useMDMFilters(
  selectedFieldDef: string,
  selectedTenant: string,
  initialFilters?: Partial<ValueListFilters>
): UseMDMFiltersReturn {
  const [filters, setFilters] = useState<ValueListFilters>({
    fielddef_id: selectedFieldDef,
    tenant_id: selectedTenant,
    include_inactive: initialFilters?.include_inactive ?? true,
    include_deleted: initialFilters?.include_deleted ?? false,
    scope: initialFilters?.scope,
  });

  const [searchTerm, setSearchTerm] = useState<string>('');

  /**
   * Handle filter changes
   *
   * Purpose: Update specific filter value
   * Logic: Merges new filter value with existing filters
   *
   * @param key - Filter key to update
   * @param value - New filter value
   */
  const handleFilterChange = useCallback((key: keyof ValueListFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  /**
   * Handle search input change
   *
   * Purpose: Update search term state from input event
   *
   * @param event - React change event from search input
   */
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  // Update filters when selection changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      fielddef_id: selectedFieldDef,
      tenant_id: selectedTenant,
    }));
  }, [selectedFieldDef, selectedTenant]);

  return {
    filters,
    searchTerm,
    setSearchTerm,
    handleFilterChange,
    handleSearchChange,
  };
}

/**
 * useMDMSelection Hook Return Type
 */
export interface UseMDMSelectionReturn {
  selectedIds: string[];
  selectValue: (id: string) => void;
  deselectValue: (id: string) => void;
  toggleSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
}

/**
 * useMDMSelection Hook
 *
 * Purpose: Manages row selection state for bulk operations
 *
 * @returns Selection state and action handlers
 *
 * @example
 * const { selectedIds, toggleSelection, clearSelection } = useMDMSelection();
 */
export function useMDMSelection(): UseMDMSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  /**
   * Select a value by ID
   *
   * @param id - Value ID to select
   */
  const selectValue = useCallback((id: string) => {
    setSelectedIds((prev) => [...prev, id]);
  }, []);

  /**
   * Deselect a value by ID
   *
   * @param id - Value ID to deselect
   */
  const deselectValue = useCallback((id: string) => {
    setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
  }, []);

  /**
   * Toggle selection state of a value
   *
   * @param id - Value ID to toggle
   */
  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  }, []);

  /**
   * Select all values
   *
   * @param ids - Array of all value IDs to select
   */
  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  /**
   * Check if a value is selected
   *
   * @param id - Value ID to check
   * @returns True if selected, false otherwise
   */
  const isSelected = useCallback(
    (id: string) => {
      return selectedIds.includes(id);
    },
    [selectedIds]
  );

  return {
    selectedIds,
    selectValue,
    deselectValue,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
  };
}

/**
 * useMDMDialogs Hook Return Type
 */
export interface UseMDMDialogsReturn {
  // Value form modal
  valueFormOpen: boolean;
  editingValue: MasterDataValue | null;
  openValueForm: (value?: MasterDataValue) => void;
  closeValueForm: () => void;
  handleValueFormSuccess: () => void;

  // Import modal
  importModalOpen: boolean;
  openImportModal: () => void;
  closeImportModal: () => void;
  handleImportSuccess: () => void;

  // Export dialog
  exportDialogOpen: boolean;
  openExportDialog: () => void;
  closeExportDialog: () => void;
  handleExportSuccess: () => void;

  // Revert override dialog
  revertDialogOpen: boolean;
  revertingValue: MasterDataValue | null;
  globalValue: MasterDataValue | null;
  revertLoading: boolean;
  openRevertDialog: (value: MasterDataValue, globalVal?: MasterDataValue | null) => void;
  closeRevertDialog: () => void;
  setGlobalValue: (value: MasterDataValue | null) => void;
  setRevertLoading: (loading: boolean) => void;
}

/**
 * useMDMDialogs Hook
 *
 * Purpose: Manages state for all modals and dialogs in MDM Manager
 *
 * @param onRefresh - Callback to refresh data after successful operations
 *
 * @returns Dialog states and control handlers
 *
 * @example
 * const {
 *   valueFormOpen,
 *   editingValue,
 *   openValueForm,
 *   closeValueForm
 * } = useMDMDialogs(refreshData);
 */
export function useMDMDialogs(onRefresh?: () => void): UseMDMDialogsReturn {
  // Value form modal states
  const [valueFormOpen, setValueFormOpen] = useState<boolean>(false);
  const [editingValue, setEditingValue] = useState<MasterDataValue | null>(null);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

  // Revert dialog states
  const [revertDialogOpen, setRevertDialogOpen] = useState<boolean>(false);
  const [revertingValue, setRevertingValue] = useState<MasterDataValue | null>(null);
  const [globalValue, setGlobalValue] = useState<MasterDataValue | null>(null);
  const [revertLoading, setRevertLoading] = useState<boolean>(false);

  /**
   * Open value form modal
   *
   * @param value - Optional value to edit (undefined for create new)
   */
  const openValueForm = useCallback((value?: MasterDataValue) => {
    setEditingValue(value || null);
    setValueFormOpen(true);
  }, []);

  /**
   * Close value form modal
   */
  const closeValueForm = useCallback(() => {
    setValueFormOpen(false);
    setEditingValue(null);
  }, []);

  /**
   * Handle value form success
   *
   * Purpose: Close modal and refresh data after successful create/edit
   */
  const handleValueFormSuccess = useCallback(() => {
    setValueFormOpen(false);
    setEditingValue(null);
    onRefresh?.();
  }, [onRefresh]);

  /**
   * Open import modal
   */
  const openImportModal = useCallback(() => {
    setImportModalOpen(true);
  }, []);

  /**
   * Close import modal
   */
  const closeImportModal = useCallback(() => {
    setImportModalOpen(false);
  }, []);

  /**
   * Handle import success
   *
   * Purpose: Close modal and refresh data after successful import
   */
  const handleImportSuccess = useCallback(() => {
    setImportModalOpen(false);
    onRefresh?.();
  }, [onRefresh]);

  /**
   * Open export dialog
   */
  const openExportDialog = useCallback(() => {
    setExportDialogOpen(true);
  }, []);

  /**
   * Close export dialog
   */
  const closeExportDialog = useCallback(() => {
    setExportDialogOpen(false);
  }, []);

  /**
   * Handle export success
   *
   * Purpose: Close dialog after successful export
   */
  const handleExportSuccess = useCallback(() => {
    setExportDialogOpen(false);
  }, []);

  /**
   * Open revert override dialog
   *
   * @param value - Override value to revert
   * @param globalVal - Optional global value that will become visible
   */
  const openRevertDialog = useCallback(
    (value: MasterDataValue, globalVal?: MasterDataValue | null) => {
      setRevertingValue(value);
      setGlobalValue(globalVal || null);
      setRevertDialogOpen(true);
    },
    []
  );

  /**
   * Close revert override dialog
   */
  const closeRevertDialog = useCallback(() => {
    setRevertDialogOpen(false);
    setRevertingValue(null);
    setGlobalValue(null);
    setRevertLoading(false);
  }, []);

  return {
    // Value form modal
    valueFormOpen,
    editingValue,
    openValueForm,
    closeValueForm,
    handleValueFormSuccess,

    // Import modal
    importModalOpen,
    openImportModal,
    closeImportModal,
    handleImportSuccess,

    // Export dialog
    exportDialogOpen,
    openExportDialog,
    closeExportDialog,
    handleExportSuccess,

    // Revert override dialog
    revertDialogOpen,
    revertingValue,
    globalValue,
    revertLoading,
    openRevertDialog,
    closeRevertDialog,
    setGlobalValue,
    setRevertLoading,
  };
}
